import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';
import axiosInstance from '../utils/axios-auth';
import { toLocalDateKey } from '../utils/scheduleUtils';
import { supabase } from '../utils/supabase-client';
import { toUserFacingMessage } from '../utils/user-facing-api-error';
/** Merge weekly_schedule keys to lowercase day names (API/legacy data may use mixed case). */
function normalizeWeeklyScheduleForSlots(weekly) {
    if (!weekly || typeof weekly !== 'object')
        return {};
    const out = {};
    for (const [key, slots] of Object.entries(weekly)) {
        if (!Array.isArray(slots))
            continue;
        const day = key.toLowerCase();
        out[day] = [...(out[day] || []), ...slots];
    }
    return out;
}
/** Format start/end ISO strings to "HH:mm-HH:mm" for display */
function formatTimeSlotFromApi(startTime, endTime) {
    if (!startTime || !endTime)
        return '';
    try {
        const start = new Date(startTime);
        const end = new Date(endTime);
        const pad = (n) => n.toString().padStart(2, '0');
        return `${pad(start.getHours())}:${pad(start.getMinutes())}-${pad(end.getHours())}:${pad(end.getMinutes())}`;
    }
    catch {
        return '';
    }
}
export const fetch_tutor_schedule = createAsyncThunk('schedule/fetchTutorSchedule', async (tutor_id, { rejectWithValue }) => {
    // Check if tutor_id is a valid UUID-like string and not a date
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const isDatePattern = /^\d{4}-\d{2}-\d{2}$/; // Matches date format like 2025-04-28
    if (!tutor_id || isDatePattern.test(tutor_id) || !uuidPattern.test(tutor_id)) {
        return rejectWithValue('Invalid tutor ID format. Expected UUID, received possibly a date format.');
    }
    try {
        // Use the secure RPC function instead of direct table operations
        const { data, error } = await supabase.rpc('get_lesson_bookings_secure', {
            p_user_id: tutor_id,
            p_filters: { tutor_id: tutor_id },
            p_limit: 100,
            p_offset: 0
        });
        if (error)
            throw new Error(error.message);
        // The RPC function returns an object with bookings array
        const appointments = data?.bookings || [];
        // Transform the response to our app's Lesson format
        const lessons = appointments.map((appointment) => {
            const timeSlot = appointment.metadata?.time_slot || formatTimeSlotFromApi(appointment.start_time, appointment.end_time);
            const dateStr = appointment.metadata?.date || (appointment.start_time ? toLocalDateKey(new Date(appointment.start_time)) : '');
            return {
                id: appointment.id,
                tutor_id: appointment.tutor_id,
                tutor_name: appointment.tutor_name || appointment.metadata?.tutor_name || 'Tutor',
                student_id: appointment.student_id,
                student_name: appointment.student_name || appointment.metadata?.student_name || '',
                subject_id: appointment.subject_id,
                subject: appointment.subject_name || appointment.metadata?.subject_name || '',
                day: appointment.metadata?.day || '',
                time_slot: timeSlot,
                time: timeSlot,
                date: dateStr,
                start_time: appointment.start_time,
                end_time: appointment.end_time,
                suggested_start_time: appointment.suggested_start_time,
                status: appointment.status,
                notes: appointment.notes,
                meeting_link: appointment.meeting_link,
                meeting_id: appointment.meeting_id,
                metadata: appointment.metadata,
                tutor_timezone: appointment.tutor_timezone,
                student_timezone: appointment.student_timezone
            };
        });
        return lessons;
    }
    catch (error) {
        return rejectWithValue(toUserFacingMessage(error, { fallback: 'Failed to load your schedule' }));
    }
});
export const fetch_tutor_availability = createAsyncThunk('schedule/fetchTutorAvailability', async (tutor_id) => {
    try {
        // Use the public API endpoint: /api/tutors/:tutorId/availability
        const response = await axiosInstance.get(`/tutors/${tutor_id}/availability`);
        if (response.data.success && response.data.data) {
            const availabilityData = response.data.data;
            const weeklyNormalized = normalizeWeeklyScheduleForSlots(availabilityData.weekly_schedule);
            // Create available_slots from weekly_schedule for the UI
            const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
            const available_slots = [];
            daysOfWeek.forEach(day => {
                const daySlots = weeklyNormalized[day] || [];
                daySlots.forEach(slot => {
                    available_slots.push({
                        ...slot,
                        day_of_week: day,
                        tutor_id: tutor_id
                    });
                });
            });
            return {
                ...availabilityData,
                weekly_schedule: weeklyNormalized,
                available_slots
            };
        }
        else {
            // If no availability record exists, return a default structure
            const defaultAvailability = {
                tutor_id: tutor_id,
                weekly_schedule: {},
                date_overrides: [],
                timezone: 'UTC',
                lesson_duration: 60,
                break_between_lessons: 15,
                is_active: true,
                available_slots: []
            };
            return defaultAvailability;
        }
    }
    catch (error) {
        // Handle network errors gracefully - don't log network errors as they're often temporary
        if (isAxiosError(error)) {
            // Only log non-network errors for debugging
            if (error.code !== 'ERR_NETWORK' && error.response) {
                console.error('Error fetching tutor availability:', error);
                console.error('Response status:', error.response?.status);
                console.error('Response data:', error.response?.data);
            }
            // For network errors, silently return default structure
        }
        else {
            // Log non-axios errors
            console.error('Error fetching tutor availability:', error);
        }
        // If API call fails, return a default structure (graceful degradation)
        const defaultAvailability = {
            tutor_id: tutor_id,
            weekly_schedule: {},
            date_overrides: [],
            timezone: 'UTC',
            lesson_duration: 60,
            break_between_lessons: 15,
            is_active: true,
            available_slots: []
        };
        return defaultAvailability;
    }
});
export const update_tutor_availability = createAsyncThunk('schedule/updateTutorAvailability', async (data) => {
    const tutor_id = data.tutor_id;
    if (!tutor_id) {
        throw new Error('Tutor ID is required');
    }
    try {
        // Use RPC to bypass RLS instead of direct table operations
        // This avoids the 406 error and eliminates the need for separate GET/INSERT/UPDATE calls
        const { data: rpcData, error: rpcError } = await supabase.rpc('upsert_tutor_availability', {
            p_tutor_id: tutor_id,
            p_weekly_schedule: data.weekly_schedule || {},
            p_date_overrides: data.date_overrides || [],
            p_timezone: data.timezone || 'UTC',
            p_lesson_duration: data.lesson_duration || 60,
            p_break_between_lessons: data.break_between_lessons || 15,
            p_is_active: data.is_active !== undefined ? data.is_active : true
        });
        if (rpcError) {
            throw new Error(rpcError.message);
        }
        // For backward compatibility, create available_slots from weekly_schedule
        const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const available_slots = [];
        // Parse the result which is returned as JSON from the RPC function
        const result = rpcData;
        daysOfWeek.forEach(day => {
            const daySlots = result.weekly_schedule && result.weekly_schedule[day]
                ? result.weekly_schedule[day]
                : [];
            daySlots.forEach((slot) => {
                available_slots.push({
                    ...slot,
                    day_of_week: day,
                    tutor_id: tutor_id
                });
            });
        });
        return {
            ...result,
            available_slots
        };
    }
    catch (error) {
        console.error('Error in update_tutor_availability:', error);
        throw error;
    }
});
export const book_lesson = createAsyncThunk('schedule/bookLesson', async (lesson) => {
    // Use the API endpoint instead of direct Supabase calls
    const response = await axiosInstance.post('/lessons/book', {
        tutor_id: lesson.tutor_id,
        start_time: lesson.start_time,
        end_time: lesson.end_time,
        subject_id: lesson.subject_id || null,
        notes: lesson.notes,
        student_timezone: lesson.student_timezone,
        tutor_timezone: lesson.tutor_timezone,
        metadata: {
            student_name: lesson.student_name,
            subject_name: lesson.subject,
            day: lesson.day,
            time_slot: lesson.time_slot,
            date: lesson.date
        }
    });
    const responseData = response.data.data;
    // Transform the response back to our app's Lesson format
    return {
        id: responseData.id,
        tutor_id: responseData.tutor_id,
        student_id: responseData.student_id,
        student_name: responseData.metadata?.student_name || '',
        subject_id: responseData.subject_id,
        subject: responseData.metadata?.subject_name || '',
        day: responseData.metadata?.day || '',
        time_slot: responseData.metadata?.time_slot || '',
        date: responseData.metadata?.date || '',
        start_time: responseData.start_time,
        end_time: responseData.end_time,
        suggested_start_time: responseData.suggested_start_time,
        status: responseData.status,
        notes: responseData.notes,
        tutor_timezone: responseData.tutor_timezone ?? lesson.tutor_timezone,
        student_timezone: responseData.student_timezone ?? lesson.student_timezone
    };
});
export const fetch_student_lessons = createAsyncThunk('schedule/fetchStudentLessons', async (student_id) => {
    try {
        // Use the secure RPC function instead of direct table operations
        const { data, error } = await supabase.rpc('get_lesson_bookings_secure', {
            p_user_id: student_id,
            p_filters: { student_id: student_id },
            p_limit: 100,
            p_offset: 0
        });
        if (error) {
            console.error('Error fetching student lessons:', error);
            throw new Error(error.message);
        }
        // The RPC function returns an object with bookings array (enriched with tutor_name, subject_name)
        const appointments = data?.bookings || [];
        // Transform the response to our app's Lesson format
        const lessons = appointments.map((appointment) => {
            const timeSlot = appointment.metadata?.time_slot || formatTimeSlotFromApi(appointment.start_time, appointment.end_time);
            const dateStr = appointment.metadata?.date || (appointment.start_time ? toLocalDateKey(new Date(appointment.start_time)) : '');
            return {
                id: appointment.id,
                tutor_id: appointment.tutor_id,
                tutor_name: appointment.tutor_name || appointment.metadata?.tutor_name || 'Tutor',
                student_id: appointment.student_id,
                student_name: appointment.student_name || appointment.metadata?.student_name || '',
                subject_id: appointment.subject_id,
                subject: appointment.subject_name || appointment.metadata?.subject_name || '',
                day: appointment.metadata?.day || '',
                time_slot: timeSlot,
                time: timeSlot,
                date: dateStr,
                start_time: appointment.start_time,
                end_time: appointment.end_time,
                suggested_start_time: appointment.suggested_start_time,
                status: appointment.status,
                notes: appointment.notes,
                meeting_link: appointment.meeting_link,
                meeting_id: appointment.meeting_id,
                metadata: appointment.metadata,
                tutor_timezone: appointment.tutor_timezone,
                student_timezone: appointment.student_timezone
            };
        });
        return lessons;
    }
    catch (err) {
        console.error('Error in fetch_student_lessons:', err);
        throw err;
    }
});
// Function to update lesson booking status
export const update_lesson_booking_status = createAsyncThunk('schedule/updateLessonBookingStatus', async ({ booking_id, status, notes }, { rejectWithValue, getState }) => {
    try {
        const state = getState();
        const userId = state.auth?.user?.id;
        if (!userId) {
            return rejectWithValue('User not authenticated');
        }
        const { data, error } = await supabase.rpc('update_lesson_booking_status_secure', {
            p_user_id: userId,
            p_booking_id: booking_id,
            p_status: status,
            p_notes: notes || null
        });
        if (error)
            throw new Error(error.message);
        return data;
    }
    catch (error) {
        return rejectWithValue(toUserFacingMessage(error, { fallback: 'Failed to update booking status' }));
    }
});
// Function to cancel an appointment
export const cancel_appointment = createAsyncThunk('schedule/cancelAppointment', async (appointment_id) => {
    // Get the current user ID for the RPC function
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
        throw new Error('User not authenticated');
    }
    // Use the secure RPC function instead of direct table operations
    const { data, error } = await supabase.rpc('cancel_lesson_booking_secure', {
        p_user_id: user.id,
        p_booking_id: appointment_id,
        p_cancellation_reason: 'Cancelled by user'
    });
    if (error)
        throw new Error(error.message);
    // Transform the response back to our app's Lesson format
    const responseData = data;
    return {
        id: responseData.id,
        tutor_id: responseData.tutor_id,
        student_id: responseData.student_id,
        student_name: responseData.metadata?.student_name || '',
        subject_id: responseData.subject_id,
        subject: responseData.metadata?.subject_name || '',
        day: responseData.metadata?.day || '',
        time_slot: responseData.metadata?.time_slot || '',
        date: responseData.metadata?.date || '',
        start_time: responseData.start_time,
        end_time: responseData.end_time,
        suggested_start_time: responseData.suggested_start_time,
        status: responseData.status,
        notes: responseData.notes
    };
});
// Function to get first lesson pricing for a student-tutor pair
export const get_first_lesson_pricing = createAsyncThunk('schedule/getFirstLessonPricing', async ({ tutor_id, student_id }) => {
    try {
        const response = await axiosInstance.get(`/api/tutors/${tutor_id}/first-lesson-pricing`);
        return response.data.data;
    }
    catch (error) {
        if (isAxiosError(error)) {
            throw new Error(error.response?.data?.message || 'Failed to get first lesson pricing');
        }
        throw new Error('An unexpected error occurred');
    }
});
// Add export for backward compatibility
export const updateAvailability = update_tutor_availability;
const initialState = {
    tutor_availability: null,
    tutor_schedule: [],
    booked_lessons: [],
    first_lesson_pricing: null,
    is_loading: false,
    error: null,
    status: 'idle',
};
const schedule_slice = createSlice({
    name: 'schedule',
    initialState,
    reducers: {
        clear_schedule_errors: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetch_tutor_schedule.pending, (state) => {
            state.is_loading = true;
            state.status = 'loading';
            state.error = null;
        })
            .addCase(fetch_tutor_schedule.fulfilled, (state, action) => {
            state.is_loading = false;
            state.status = 'succeeded';
            state.tutor_schedule = action.payload;
        })
            .addCase(fetch_tutor_schedule.rejected, (state, action) => {
            state.is_loading = false;
            state.status = 'failed';
            state.error = action.error.message || 'Failed to fetch schedule';
        })
            .addCase(fetch_tutor_availability.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(fetch_tutor_availability.fulfilled, (state, action) => {
            state.is_loading = false;
            state.tutor_availability = action.payload;
        })
            .addCase(fetch_tutor_availability.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.error.message || 'Failed to fetch availability';
        })
            .addCase(update_tutor_availability.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(update_tutor_availability.fulfilled, (state, action) => {
            state.is_loading = false;
            state.tutor_availability = action.payload;
        })
            .addCase(update_tutor_availability.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.error.message || 'Failed to update availability';
        })
            .addCase(book_lesson.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(book_lesson.fulfilled, (state, action) => {
            state.is_loading = false;
            state.booked_lessons = [...state.booked_lessons, action.payload];
        })
            .addCase(book_lesson.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.error.message || 'Failed to book lesson';
        })
            .addCase(fetch_student_lessons.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(fetch_student_lessons.fulfilled, (state, action) => {
            state.is_loading = false;
            state.booked_lessons = action.payload;
        })
            .addCase(fetch_student_lessons.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.error.message || 'Failed to fetch lessons';
        })
            .addCase(cancel_appointment.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(cancel_appointment.fulfilled, (state, action) => {
            state.is_loading = false;
            // Update the lesson status in the booked_lessons array
            state.booked_lessons = state.booked_lessons.map(lesson => lesson.id === action.payload.id ? action.payload : lesson);
        })
            .addCase(cancel_appointment.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.error.message || 'Failed to cancel appointment';
        })
            .addCase(update_lesson_booking_status.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(update_lesson_booking_status.fulfilled, (state, action) => {
            state.is_loading = false;
            // Update the lesson status in both tutor_schedule and booked_lessons arrays
            const updatedLesson = action.payload;
            if (updatedLesson) {
                // Update in tutor_schedule
                const tutorIndex = state.tutor_schedule.findIndex(lesson => lesson.id === updatedLesson.id);
                if (tutorIndex !== -1) {
                    state.tutor_schedule[tutorIndex] = { ...state.tutor_schedule[tutorIndex], ...updatedLesson };
                }
                // Update in booked_lessons
                const bookedIndex = state.booked_lessons.findIndex(lesson => lesson.id === updatedLesson.id);
                if (bookedIndex !== -1) {
                    state.booked_lessons[bookedIndex] = { ...state.booked_lessons[bookedIndex], ...updatedLesson };
                }
            }
        })
            .addCase(update_lesson_booking_status.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.error.message || 'Failed to update lesson status';
        })
            .addCase(get_first_lesson_pricing.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(get_first_lesson_pricing.fulfilled, (state, action) => {
            state.is_loading = false;
            state.first_lesson_pricing = action.payload;
        })
            .addCase(get_first_lesson_pricing.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.error.message || 'Failed to get first lesson pricing';
        });
    },
});
export const { clear_schedule_errors } = schedule_slice.actions;
export default schedule_slice.reducer;
